import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Input, Select, Table } from "antd";
import Menubar from "../../../components/WhrManager/App";
import { http } from "../../../services/http";
import { SearchOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import { columnFinishedSales } from "../../../utils/columns";
import FinishedSalesModalWrh from "../../../components/WhrManager/finishedSalesModal";
import moment from "moment";
import Navbar from "../../../components/navbar";

const today = moment().format("YYYY-MM-DD");

const FinishedSales = () => {
  const { t } = useTranslation();
  const tableContainerRef = useRef(null);
  const isLoadingRef = useRef(false);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const [filters, setFilters] = useState({
    cardName: "",
    docNum: "",
    slpName: "",
    orderStatus: "",
    docStatus: "",
    startDate: "",
    endDate: today,
  });

  const [total, setTotal] = useState(0);
  const [managerList, setManagerList] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const orderStatusOptions = [
    { label: t("all"), value: "" },
    { label: t("confirmed"), value: "Tasdiqlandi" },
    { label: t("assigned"), value: "Biriktirildi" },
    { label: t("loadedOrder"), value: "BuyurtmaYuklandi" },
    { label: t("loadConfirmed"), value: "YuklashTasdiqlandi" },
  ];

  /** Fetch Data */
  const fetchData = useCallback(
    async (page, loadMore = false) => {
      if (isLoadingRef.current) return;

      isLoadingRef.current = true;
      loadMore ? setIsLoadingMore(true) : setLoading(true);

      try {
        const query = new URLSearchParams({
          ...filters,
          pageToken: page,
          pageSize: 30,
        }).toString();

        const { data } = await http.get(`api/yakunlangansotuvlar?${query}`);

        const newData = data?.docs || [];

        setDataSource(page === 1 ? newData : [...newData]);
        setTotal(data?.total || 0);
        setHasMoreData(newData.length === 30);
      } catch (err) {
        console.log(err);
        setHasMoreData(false);
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        isLoadingRef.current = false;
      }
    },
    [filters],
  );

  /** Trigger fetch when filters or page changes */
  useEffect(() => {
    fetchData(currentPage, currentPage > 1);
  }, [currentPage, filters]);

  /** Managers */
  useEffect(() => {
    http
      .get(`api/sales/warehouse-employee-info?skip=0`)
      .then((res) => setManagerList(res.data.data || []))
      .catch(console.log);
  }, []);

  /** Infinite scrolling */
  const debouncedNextPage = useMemo(
    () => debounce(() => setCurrentPage((p) => p + 1), 300),
    [],
  );

  const handleScroll = useCallback(
    (e) => {
      if (!hasMoreData || loading || isLoadingMore || isLoadingRef.current)
        return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        debouncedNextPage();
      }
    },
    [hasMoreData, loading, isLoadingMore],
  );

  useEffect(() => {
    const tbl = tableContainerRef.current?.querySelector(".ant-table-body");
    if (tbl) {
      tbl.addEventListener("scroll", handleScroll);
      return () => tbl.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  /** Debounced search */
  const debouncedFilter = useMemo(
    () =>
      debounce((value, key, confirm) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
        confirm();
      }, 600),
    [],
  );

  /** Column search props */
  const getColumnSearchProps = (dataIndex) => {
    if (["orderStatus", "docStatus"].includes(dataIndex)) {
      const opts =
        dataIndex === "orderStatus"
          ? orderStatusOptions
          : [
              { value: "", label: t("all") },
              { value: "O", label: t("open") },
              { value: "C", label: t("closed") },
            ];

      return {
        filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => (
          <div style={{ padding: 8 }}>
            <Select
              value={selectedKeys[0] || ""}
              onChange={(v) => {
                setSelectedKeys([v]);
                debouncedFilter(v, dataIndex, confirm);
              }}
              style={{ width: 180 }}
              options={opts}
            />
          </div>
        ),
        filterIcon: (f) => (
          <SearchOutlined style={{ color: f ? "#1890ff" : undefined }} />
        ),
      };
    }

    return {
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t("search")}
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys([e.target.value]);
              debouncedFilter(e.target.value, dataIndex, confirm);
            }}
            style={{ width: 180 }}
          />
        </div>
      ),
      filterIcon: (f) => (
        <SearchOutlined style={{ color: f ? "#1890ff" : undefined }} />
      ),
    };
  };

  /** Columns */
  const columns = useMemo(() => {
    return columnFinishedSales(t, getColumnSearchProps).map((col) => {
      if (col.key === "responsiblePerson") {
        return {
          ...col,
          render: (_, record) => {
            const m = managerList.find(
              (x) => x.employeeId === Number(record.responsiblePerson),
            );
            return `${m?.firstName || ""} ${m?.lastName || ""}` || "-";
          },
        };
      }

      if (col.key === "orderStatus") {
        return {
          ...col,
          render: (text, rec) => {
            const st = orderStatusOptions.find(
              (s) => s.value === rec.orderStatus,
            );
            return st?.label || "-";
          },
        };
      }

      if (col.key === "docStatus") {
        return {
          ...col,
          render: (text) =>
            text === "O" ? t("open") : text === "C" ? t("closed") : text,
        };
      }

      return col;
    });
  }, [managerList, filters]);

  return (
    <div className="flex h-full w-full overflow-y-auto">
      <Menubar />
      <div className="h-screen w-full overflow-y-auto">
        <Navbar />

        <h1 className="font-poppins ml-4 mt-10 text-xl font-bold text-black sm:ml-5 sm:mt-14 sm:text-2xl">
          {t("finishedSales")}
        </h1>

        <div className="mt-10 w-full border border-[#E8E8E8]"></div>

        {/* Date filters */}
        <div className="ml-5 mt-5 flex gap-5">
          <div className="flex flex-col gap-3">
            <p>{t("startDate")}</p>
            <Input
              type="date"
              value={filters.startDate}
              className="w-[150px]"
              onChange={(e) =>
                setFilters((p) => ({ ...p, startDate: e.target.value }))
              }
              max={filters.endDate}
            />
          </div>
          <div className="flex flex-col gap-3">
            <p>{t("endDate")}</p>
            <Input
              type="date"
              value={filters.endDate}
              className="w-[150px]"
              onChange={(e) =>
                setFilters((p) => ({ ...p, endDate: e.target.value }))
              }
              min={filters.startDate}
              max={today}
            />
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="mb-4 flex flex-col justify-between sm:flex-row">
          <div className="font-nunitto pl-5 pt-5 font-bold">
            {t("page")}: {currentPage}
          </div>
          <div className="mt-2 flex gap-2 sm:mr-10">
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="h-[40px] w-[120px] rounded-2xl bg-gray-300 text-gray-700"
            >
              {t("previous")}
            </Button>
            <Button
              onClick={() => setCurrentPage((p) => p + 1)}
              className="h-[40px] w-[120px] rounded-2xl bg-[#0A4D68] text-white"
            >
              {t("next")}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-10 overflow-x-auto" ref={tableContainerRef}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            loading={loading || isLoadingMore}
            rowKey={(r) => r.docEntry}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRowData(record);
                setIsModalVisible(true);
              },
            })}
            sticky={{ offsetHeader: 0, getContainer: () => window }}
            summary={() => (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{t("total")}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>
                      {new Intl.NumberFormat("fr-FR").format(total)} USD
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      </div>

      <FinishedSalesModalWrh
        visible={isModalVisible}
        data={selectedRowData}
        onClose={() => setIsModalVisible(false)}
      />
    </div>
  );
};

export default FinishedSales;
